import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Bug } from "lucide-react";
import { SUPPORT_EMAIL, supportApi } from "../../api/support.api";
import { studentErrorMessage } from "./student-shared";
import { BackButton } from "./parts";

const MIN_LENGTH = 5;
const MAX_LENGTH = 4000;

/**
 * Signalement d'un problème au service technique Permio, depuis la messagerie.
 * Ce n'est pas une conversation : le texte part par e-mail, et la réponse arrive par e-mail.
 */
export function BugReport({
  page,
  onClose,
}: {
  /** Page de l'application d'où part le signalement (chemin seul), lue à l'envoi. */
  page: () => string;
  onClose: () => void;
}) {
  const [message, setMessage] = useState("");
  const text = message.trim();
  const send = useMutation({
    mutationFn: () => {
      const from = page();
      return supportApi.report({
        message: text,
        // Un chemin simple seulement : ni paramètres, ni adresse complète.
        ...(/^\/[^\s?#]{0,299}$/.test(from) ? { page: from } : {}),
      });
    },
  });
  const back = <BackButton onBack={onClose} />;
  if (send.isSuccess)
    return (
      <section className="messaging-report">
        <header className="messaging-conv-head">
          {back}
          <div className="messaging-conv-title">
            <h2>Signalement envoyé</h2>
          </div>
        </header>
        <div className="messaging-state-box">
          <p role="status">
            Merci. Votre message est parti au service technique Permio.{" "}
            {send.data.replyTo
              ? `Nous vous répondrons par e-mail à ${send.data.replyTo}.`
              : "Nous vous répondrons par e-mail."}
          </p>
          <div className="messaging-state-actions">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onClose}
            >
              Retour aux conversations
            </button>
          </div>
        </div>
      </section>
    );
  return (
    <section className="messaging-report">
      <header className="messaging-conv-head">
        {back}
        <div className="messaging-conv-title">
          <h2>Signaler un problème</h2>
          <p className="messaging-conv-sub">
            <span className="messaging-report-mark">
              <Bug size={12} aria-hidden="true" />
              Service technique Permio
            </span>
          </p>
        </div>
      </header>
      <form
        className="messaging-form messaging-report-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (text.length >= MIN_LENGTH && !send.isPending) send.mutate();
        }}
      >
        <label>
          Que s’est-il passé ?
          <textarea
            rows={6}
            required
            minLength={MIN_LENGTH}
            maxLength={MAX_LENGTH}
            placeholder="Ce que vous faisiez, ce qui s’est passé, ce que vous attendiez."
            value={message}
            readOnly={send.isPending}
            onChange={(event) => {
              if (send.isError) send.reset();
              setMessage(event.target.value);
            }}
          />
        </label>
        <p className="messaging-hint">
          Votre message part par e-mail au service technique Permio, avec votre
          nom, votre adresse e-mail, votre école, la page ouverte et votre
          navigateur. Rien d’autre n’est joint : ne collez pas de données
          d’élève dont nous n’aurions pas besoin.
        </p>
        {send.isError && (
          <p role="alert">
            {studentErrorMessage(
              send.error,
              `Le signalement n’a pas pu être envoyé. Écrivez-nous à ${SUPPORT_EMAIL}.`,
            )}{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>Ouvrir un e-mail</a>
          </p>
        )}
        <div className="messaging-form-actions">
          <button
            type="button"
            className="btn btn--ghost"
            disabled={send.isPending}
            onClick={onClose}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="btn btn--primary"
            disabled={text.length < MIN_LENGTH || send.isPending}
          >
            {send.isPending ? "Envoi…" : "Envoyer le signalement"}
          </button>
        </div>
      </form>
    </section>
  );
}
